import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { LIMIT_PAGE } from "Shared/Constants/app";

/** The last page that `count` records fill; 1 when there are none. */
export const lastPage = (count: number, limit = LIMIT_PAGE) =>
  Math.max(1, Math.ceil(count / limit));

/**
 * Moves `page` back to the last page once a loaded `count` shows it is past
 * the end, e.g. after deleting the only row on the last page or opening a
 * stale link. The pager can't get the user out: it already draws the last
 * page as the current one, so there is nothing left to click.
 *
 * `ready` must be true only while `count` belongs to the current request.
 */
export const usePageInRange = (
  page: number,
  setPage: (page: number) => void,
  count: number,
  ready: boolean,
  limit = LIMIT_PAGE,
) => {
  const last = lastPage(count, limit);
  useEffect(() => {
    if (ready && page > last) setPage(last);
  }, [ready, page, last, setPage]);
};

const positiveInt = (value: string | null) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
};

/**
 * A page number kept in the URL under `param`, held inside the loaded range.
 * Lists sharing a URL (tabs on one detail page) need a `param` each, so one
 * list's page never carries over to another.
 */
export const useUrlPage = (
  param: string,
  count: number,
  ready: boolean,
  limit = LIMIT_PAGE,
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = positiveInt(searchParams.get(param));
  const setPage = useCallback(
    (next: number) =>
      setSearchParams(
        (current) => {
          const updated = new URLSearchParams(current);
          if (next > 1) updated.set(param, String(next));
          else updated.delete(param);
          return updated;
        },
        { replace: true },
      ),
    [param, setSearchParams],
  );
  usePageInRange(page, setPage, count, ready, limit);
  return page;
};
